// ----------------------------------------------------------------------------
// DEV PREVIEW: ?demomeeting=N injects N fake LIVE meetings (each with a fake NEXT
// session ~30min out) into a WorldSnapshot so the conference-table gather, the
// per-room board (current + next), and the "in meeting room" desk signs can be
// verified visually before a real meeting ever fires.
//
// Why snapshot-level: computeLayout(), the board, the desk signs and the
// in-meeting Character flags ALL key off WorldSnapshot.meetings via
// liveMeetings()/inMeetingUserIds()/upcomingMeetings(). Injecting here means
// every downstream consumer treats the fake sessions exactly like real ones — no
// special-casing in world.ts / OfficeScene.ts. The injected LIVE sessions pass
// isMeetingLive() verbatim (status ACTIVE, invite_pulse true, type MEETING,
// companion in_meeting, started in the past).
//
// INERT GUARANTEE: withDemoMeetings() returns the SAME snapshot object (===)
// untouched when the flag is absent or <= 0, so the real-data pipeline is
// byte-for-byte unchanged in normal use. Read once, off by default, no storage.
// ----------------------------------------------------------------------------
import type { Meeting, Person, WorldSnapshot } from '~/data/types'

/** Read ?demomeeting=N from the URL. Returns the meeting count (0 = off). */
export function demoMeetingCount(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): number {
  try {
    const raw = new URLSearchParams(search).get('demomeeting')
    if (raw == null) return 0
    // Bare ?demomeeting (no value) means "one meeting".
    const n = raw === '' ? 1 : Number.parseInt(raw, 10)
    if (!Number.isFinite(n) || n <= 0) return 0
    return Math.min(n, 3) // cap at the bank of 3 shared rooms (MEETING_TABLES)
  } catch {
    return 0
  }
}

// A handful of believable names so each demo room reads distinctly and the
// board's title isn't a repeated string.
let demoAnchorMs: number | undefined

const DEMO_NAMES = [
  'Sprint Planning',
  'Design Review',
  'Roadmap Sync',
  'Standup',
  'Growth Weekly',
]
const NEXT_NAMES = ['Retro', '1:1 Sync', 'Demo Day', 'Backlog Grooming', 'All-Hands']

/**
 * Which userIds are actually seat-able at a conference table: world.ts only
 * gathers attendees for whom activeRoom.has(u) is true — i.e. non-INVITED people
 * who are members of a drawn TEAM pulse (one with members). Mirror that rule
 * exactly so the fake attendees really sit down instead of being silently
 * dropped. Online people are preferred (more lifelike), then anyone eligible.
 */
function seatableUserIds(snapshot: WorldSnapshot): string[] {
  const teamMemberIds = new Set<string>()
  for (const p of snapshot.pulses) {
    if (p.category !== 'TEAM' || p.memberUserIds.length === 0) continue
    for (const uid of p.memberUserIds) teamMemberIds.add(uid)
  }
  const eligible = snapshot.people.filter(
    (p: Person) => p.status !== 'INVITED' && teamMemberIds.has(p.userId),
  )
  const online = eligible.filter((p) => p.presence === 'online').map((p) => p.userId)
  const rest = eligible.filter((p) => p.presence !== 'online').map((p) => p.userId)
  return [...online, ...rest]
}

/** Build one real-looking LIVE meeting session from a slice of attendee ids. */
function makeLiveMeeting(
  index: number,
  attendeeUserIds: string[],
  pulseId: string | null,
  nowMs: number,
): Meeting {
  return {
    id: `demo-meeting-${index + 1}`,
    name: DEMO_NAMES[index % DEMO_NAMES.length],
    pulseId,
    // Started a few minutes ago (must be <= now to pass isMeetingLive's
    // startAt > now guard); staggered so the board's ordering is deterministic.
    startAt: new Date(nowMs - (8 + index * 4) * 60_000).toISOString(),
    endAt: new Date(nowMs + 30 * 60_000).toISOString(),
    attendeeUserIds,
    invitedUserIds: attendeeUserIds,
    status: 'ACTIVE',
    companionStatus: 'in_meeting',
    invitePulse: true,
    type: 'MEETING',
  }
}

/** Build the NEXT (not-yet-live) session for a room so the board shows current
 *  AND next. Same pulse, start ~30min in the future → upcomingMeetings picks it,
 *  isMeetingLive rejects it (future start). */
function makeNextMeeting(index: number, pulseId: string | null, nowMs: number): Meeting {
  return {
    id: `demo-next-${index + 1}`,
    name: NEXT_NAMES[index % NEXT_NAMES.length],
    pulseId,
    startAt: new Date(nowMs + (30 + index * 5) * 60_000).toISOString(),
    endAt: new Date(nowMs + (60 + index * 5) * 60_000).toISOString(),
    attendeeUserIds: [],
    invitedUserIds: [],
    status: 'ACTIVE',
    companionStatus: null,
    invitePulse: true,
    type: 'MEETING',
  }
}

/**
 * Return a snapshot with N fake LIVE meetings (+ matching NEXT sessions) appended
 * to .meetings, drawing real attendee ids from the loaded roster so they seat at
 * the tables. When the flag is off (or no one is seat-able) the ORIGINAL snapshot
 * is returned unchanged — a strict no-op outside dev preview.
 */
export function withDemoMeetings(
  snapshot: WorldSnapshot,
  count = demoMeetingCount(),
): WorldSnapshot {
  if (count <= 0) return snapshot

  const pool = seatableUserIds(snapshot)
  if (pool.length === 0) {
    // Nobody can be seated (e.g. no team rooms loaded) — don't fabricate a ghost
    // meeting with no attendees; world.ts would skip it anyway.
    // eslint-disable-next-line no-console
    console.warn('[demomeeting] no seat-able people in this org — nothing to inject')
    return snapshot
  }

  // Map an attendee back to their home TEAM pulse so the demo pulseId is honest.
  const pulseOfUser = new Map<string, string>()
  for (const p of snapshot.pulses) {
    if (p.category !== 'TEAM') continue
    for (const uid of p.memberUserIds) if (!pulseOfUser.has(uid)) pulseOfUser.set(uid, p.id)
  }

  // Anchor timestamps once per session so a meetingRefetch re-injection keeps
  // the SAME demo timestamps (ids are already stable) instead of drifting.
  demoAnchorMs ??= Date.now()
  const nowMs = demoAnchorMs
  const meetings: Meeting[] = []
  let cursor = 0
  for (let i = 0; i < count && cursor < pool.length; i += 1) {
    // 2–4 attendees per meeting, capped by the 8 baked chairs and what's left.
    const want = Math.min(2 + (i % 3), 8, pool.length - cursor)
    const attendees = pool.slice(cursor, cursor + want)
    cursor += want
    if (attendees.length === 0) break
    const pulseId = pulseOfUser.get(attendees[0]) ?? null
    meetings.push(makeLiveMeeting(i, attendees, pulseId, nowMs))
    meetings.push(makeNextMeeting(i, pulseId, nowMs))
  }
  if (meetings.length === 0) return snapshot

  // New array + object so React-Query's cached snapshot is never mutated.
  return { ...snapshot, meetings: [...snapshot.meetings, ...meetings] }
}
